import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ListQueryParams } from '../api/apiUtils';
import type { AllowedPageSize } from '../components/table/ListPagination';

export const SEARCH_DEBOUNCE_MS = 300;

const ALLOWED_PAGE_SIZES: readonly AllowedPageSize[] = [25, 50, 100];

const SORT_STORAGE_PREFIX = 'authorai.lastSort.';

type SortDir = 'asc' | 'desc';

function readPersistedSort(storageKey: string | undefined): { key: string; dir: SortDir } | null {
  if (!storageKey) return null;
  try {
    const raw = window.localStorage.getItem(`${SORT_STORAGE_PREFIX}${storageKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { key?: unknown; dir?: unknown };
    if (typeof parsed.key !== 'string') return null;
    if (parsed.dir !== 'asc' && parsed.dir !== 'desc') return null;
    return { key: parsed.key, dir: parsed.dir };
  } catch {
    return null;
  }
}

function writePersistedSort(storageKey: string | undefined, key: string, dir: SortDir): void {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(`${SORT_STORAGE_PREFIX}${storageKey}`, JSON.stringify({ key, dir }));
  } catch {
    // Ignore storage failures (private mode / quota); sort just won't persist across sessions.
  }
}

interface UseListControlsOptions<TSortKey extends string> {
  defaultSort: TSortKey;
  /** Valid sort keys, used to fall back to `defaultSort` for a stale/hand-edited URL. */
  sortKeys?: readonly TSortKey[];
  defaultDir?: SortDir;
  defaultPageSize?: AllowedPageSize;
  assignedAccountId?: string;
  /**
   * When set, the last sort a user chose for this list persists across
   * sessions (`localStorage`), used as the fallback whenever the URL has no
   * `sort` param — e.g. arriving fresh via the sidebar. Unlike search/page,
   * sort is a standing display preference, not part of a browsing "trip".
   */
  sortStorageKey?: string;
}

interface ListControls<TSortKey extends string> {
  searchInput: string;
  setSearchInput: (value: string) => void;
  sortKey: TSortKey;
  sortDir: SortDir;
  handleSort: (key: TSortKey) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: AllowedPageSize;
  setPageSize: (size: AllowedPageSize) => void;
  /** Selected tag ids (as strings, matching the URL) — empty when unfiltered. */
  tagIds: string[];
  setTagIds: (tagIds: string[]) => void;
  queryParams: ListQueryParams;
  /** Whether search/sort/page/pageSize currently differ from their defaults. */
  hasNonDefaultState: boolean;
  /** Clears search/sort/page/pageSize back to their defaults in one action. */
  resetToDefaults: () => void;
}

/**
 * Shared state for the server-driven list pages (Lessons, Courses,
 * Questions): debounced search, sort, and pagination — persisted in the URL
 * query string (`q`, `sort`, `dir`, `page`, `pageSize`) so it survives
 * navigating to a detail view and back, whether via an in-app back action,
 * browser back/forward, or a full reload. `queryParams` is the object to hand
 * to the list query hook.
 *
 * Sort is additionally treated as a standing preference rather than part of
 * that per-visit state: pass `sortStorageKey` to persist the last chosen sort
 * across sessions (`localStorage`), used whenever the URL has no `sort` param
 * of its own (e.g. a fresh visit via the sidebar). `resetToDefaults` and
 * `hasNonDefaultState` deliberately ignore sort for the same reason — Reset
 * clears search/page, not a user's display preference.
 *
 * Generic over the sort-key union so each page keeps its own sortable columns.
 */
export function useListControls<TSortKey extends string>({
  defaultSort,
  sortKeys,
  defaultDir = 'desc',
  defaultPageSize = 25,
  assignedAccountId,
  sortStorageKey,
}: UseListControlsOptions<TSortKey>): ListControls<TSortKey> {
  const [searchParams, setSearchParams] = useSearchParams();

  // React Router recomputes `setSearchParams`'s identity whenever the URL
  // changes, unlike a `useState` setter. Route callers' setters (`setPage`,
  // `handleSort`, ...) through this ref so THEY stay referentially stable
  // across renders — callers may depend on them in effect dependency arrays.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const isValidSortKey = useCallback(
    (key: string): key is TSortKey => !sortKeys || sortKeys.includes(key as TSortKey),
    [sortKeys],
  );

  const resolveSort = useCallback((params: URLSearchParams): { key: TSortKey; dir: SortDir } => {
    const rawKey = params.get('sort');
    if (rawKey && isValidSortKey(rawKey)) {
      const rawDir = params.get('dir');
      return { key: rawKey, dir: rawDir === 'asc' || rawDir === 'desc' ? rawDir : defaultDir };
    }
    const persisted = readPersistedSort(sortStorageKey);
    if (persisted && isValidSortKey(persisted.key)) return { key: persisted.key, dir: persisted.dir };
    return { key: defaultSort, dir: defaultDir };
  }, [defaultDir, defaultSort, isValidSortKey, sortStorageKey]);

  const { key: sortKey, dir: sortDir } = resolveSort(searchParams);

  const rawPage = Number(searchParams.get('page'));
  const page = Number.isInteger(rawPage) && rawPage >= 1 ? rawPage : 1;

  const rawPageSize = Number(searchParams.get('pageSize'));
  const pageSize = (ALLOWED_PAGE_SIZES as readonly number[]).includes(rawPageSize)
    ? (rawPageSize as AllowedPageSize)
    : defaultPageSize;

  const committedSearch = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(committedSearch);

  // The URL can change without remounting this hook (for example, browser
  // navigation or a sidebar link to the bare list route). Keep the visible
  // input aligned so the debounce below cannot restore a stale query.
  useEffect(() => {
    setSearchInput(committedSearch);
  }, [committedSearch]);

  // Reset to page 1 when the context account changes so the committed list
  // query uses page 1. Separate from the debounce/sort/page-size resets below.
  const prevAssignedAccountIdRef = useRef(assignedAccountId);
  useEffect(() => {
    if (assignedAccountId === prevAssignedAccountIdRef.current) return;
    prevAssignedAccountIdRef.current = assignedAccountId;
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    }, { replace: true });
  }, [assignedAccountId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === committedSearch) return;
      setSearchParamsRef.current((prev) => {
        const next = new URLSearchParams(prev);
        if (trimmed) next.set('q', trimmed); else next.delete('q');
        next.set('page', '1');
        return next;
      }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, committedSearch]);

  const handleSort = useCallback((key: TSortKey) => {
    setSearchParamsRef.current((prev) => {
      const active = resolveSort(prev);
      const nextDir: SortDir = active.key === key
        ? active.dir === 'asc' ? 'desc' : 'asc'
        : key === defaultSort ? defaultDir : 'asc';
      writePersistedSort(sortStorageKey, key, nextDir);
      const next = new URLSearchParams(prev);
      next.set('sort', key);
      next.set('dir', nextDir);
      next.set('page', '1');
      return next;
    }, { replace: true });
  }, [defaultDir, defaultSort, resolveSort, sortStorageKey]);

  const setPage = useCallback((nextPage: number) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(nextPage));
      return next;
    }, { replace: true });
  }, []);

  const setPageSize = useCallback((size: AllowedPageSize) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set('pageSize', String(size));
      next.set('page', '1');
      return next;
    }, { replace: true });
  }, []);

  // Repeated ?tagIds=1&tagIds=2 — getAll (not get) reads every occurrence.
  // A separate concern from search/sort/page: it has its own "Clear all"
  // affordance in the chip row (NA-398), so it's deliberately excluded from
  // hasNonDefaultState/resetToDefaults below rather than folded into them.
  const tagIds = searchParams.getAll('tagIds');

  const setTagIds = useCallback((nextTagIds: string[]) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tagIds');
      for (const tagId of nextTagIds) next.append('tagIds', tagId);
      next.set('page', '1');
      return next;
    }, { replace: true });
  }, []);

  // Sort is a standing display preference, not a filter — it's excluded from
  // both the "is this list filtered" check and the reset action below.
  const hasNonDefaultState = Boolean(searchInput.trim())
    || page !== 1
    || pageSize !== defaultPageSize;

  const resetToDefaults = useCallback(() => {
    setSearchInput('');
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      next.delete('page');
      next.delete('pageSize');
      return next;
    }, { replace: true });
  }, []);

  const queryParams = useMemo<ListQueryParams>(() => ({
    page,
    pageSize,
    sort: sortKey,
    order: sortDir,
    search: committedSearch,
    assignedAccountId,
    tagIds,
  }), [page, pageSize, sortKey, sortDir, committedSearch, assignedAccountId, tagIds]);

  return {
    searchInput,
    setSearchInput,
    sortKey,
    sortDir,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    tagIds,
    setTagIds,
    queryParams,
    hasNonDefaultState,
    resetToDefaults,
  };
}

interface PageBoundsResetParams {
  page: number;
  pageSize: number;
  totalCount: number;
  isPlaceholderData: boolean;
  setPage: (page: number) => void;
}

/**
 * When fresh data shrinks the result set below the current page (e.g. after
 * deleting the last row on a page), step back to the last page that has rows so
 * the user isn't stranded on an empty page. No-op while showing placeholder data.
 */
export function usePageBoundsReset({
  page,
  pageSize,
  totalCount,
  isPlaceholderData,
  setPage,
}: PageBoundsResetParams): void {
  useEffect(() => {
    if (isPlaceholderData) return;
    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [page, pageSize, totalCount, isPlaceholderData, setPage]);
}
